import React, { useMemo } from 'react';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { renderToStaticMarkup } from 'react-dom/server';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter markdown text...',
  className = '',
  minHeight = '200px',
  maxHeight = '500px',
}) => {
  // Configure SimpleMDE with only the required toolbar buttons and custom Material-UI icons
  const editorOptions = useMemo(
    () =>
      ({
        placeholder,
        spellChecker: false,
        toolbar: [
          {
            name: 'bold',
            action: (editor: any) => {
              const cm = editor.codemirror;
              const selection = cm.getSelection();
              const startPos = cm.getCursor('start');
              const endPos = cm.getCursor('end');

              // Get surrounding characters to check if already formatted
              const line = cm.getLine(startPos.line);
              const beforeStart2 = line.substring(Math.max(0, startPos.ch - 2), startPos.ch);
              const afterEnd2 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 2));
              const beforeStart3 = line.substring(Math.max(0, startPos.ch - 3), startPos.ch);
              const afterEnd3 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 3));

              // Check for bold+italic (***) or just bold (**)
              const isBoldItalic = beforeStart3 === '***' && afterEnd3 === '***';
              const isBold =
                (beforeStart2 === '**' && afterEnd2 === '**') ||
                (selection.startsWith('**') && selection.endsWith('**') && selection.length > 4);

              if (isBoldItalic) {
                // Remove only the bold markers (inner **), keep italic (*)
                cm.replaceRange(
                  `*${selection}*`,
                  { line: startPos.line, ch: startPos.ch - 3 },
                  { line: endPos.line, ch: endPos.ch + 3 },
                );
                cm.setSelection(
                  { line: startPos.line, ch: startPos.ch - 2 },
                  { line: endPos.line, ch: startPos.ch - 2 + selection.length },
                );
              } else if (isBold) {
                if (beforeStart2 === '**' && afterEnd2 === '**') {
                  // Remove surrounding bold markers
                  cm.replaceRange(
                    selection,
                    { line: startPos.line, ch: startPos.ch - 2 },
                    { line: endPos.line, ch: endPos.ch + 2 },
                  );
                  cm.setSelection(
                    { line: startPos.line, ch: startPos.ch - 2 },
                    { line: endPos.line, ch: startPos.ch - 2 + selection.length },
                  );
                } else {
                  // Remove bold from selection that includes markers
                  const unwrapped = selection.slice(2, -2);
                  cm.replaceSelection(unwrapped);
                  cm.setSelection(startPos, {
                    line: endPos.line,
                    ch: startPos.ch + unwrapped.length,
                  });
                }
              } else {
                // Add bold formatting
                const wrapped = `**${selection}**`;
                cm.replaceSelection(wrapped);
                cm.setSelection(
                  { line: startPos.line, ch: startPos.ch + 2 },
                  { line: endPos.line, ch: startPos.ch + 2 + selection.length },
                );
              }
            },
            className: 'markdown-bold-icon',
            title: 'Bold',
          },
          {
            name: 'italic',
            action: (editor: any) => {
              const cm = editor.codemirror;
              const selection = cm.getSelection();
              const startPos = cm.getCursor('start');
              const endPos = cm.getCursor('end');

              // Get surrounding characters to check if already formatted
              const line = cm.getLine(startPos.line);
              const beforeStart1 = line.substring(Math.max(0, startPos.ch - 1), startPos.ch);
              const afterEnd1 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 1));
              const beforeStart2 = line.substring(Math.max(0, startPos.ch - 2), startPos.ch);
              const afterEnd2 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 2));
              const beforeStart3 = line.substring(Math.max(0, startPos.ch - 3), startPos.ch);
              const afterEnd3 = line.substring(endPos.ch, Math.min(line.length, endPos.ch + 3));

              // Check for bold+italic (***) or just italic (*)
              const isBoldItalic = beforeStart3 === '***' && afterEnd3 === '***';
              const isItalic =
                (beforeStart1 === '*' &&
                  afterEnd1 === '*' &&
                  beforeStart2 !== '**' &&
                  afterEnd2 !== '**') ||
                (selection.startsWith('*') &&
                  selection.endsWith('*') &&
                  !selection.startsWith('**') &&
                  selection.length > 2);

              if (isBoldItalic) {
                // Remove only the italic markers (outer *), keep bold (**)
                cm.replaceRange(
                  `**${selection}**`,
                  { line: startPos.line, ch: startPos.ch - 3 },
                  { line: endPos.line, ch: endPos.ch + 3 },
                );
                cm.setSelection(
                  { line: startPos.line, ch: startPos.ch - 1 },
                  { line: endPos.line, ch: startPos.ch - 1 + selection.length },
                );
              } else if (isItalic) {
                if (
                  beforeStart1 === '*' &&
                  afterEnd1 === '*' &&
                  beforeStart2 !== '**' &&
                  afterEnd2 !== '**'
                ) {
                  // Remove surrounding italic markers
                  cm.replaceRange(
                    selection,
                    { line: startPos.line, ch: startPos.ch - 1 },
                    { line: endPos.line, ch: endPos.ch + 1 },
                  );
                  cm.setSelection(
                    { line: startPos.line, ch: startPos.ch - 1 },
                    { line: endPos.line, ch: startPos.ch - 1 + selection.length },
                  );
                } else {
                  // Remove italic from selection that includes markers
                  const unwrapped = selection.slice(1, -1);
                  cm.replaceSelection(unwrapped);
                  cm.setSelection(startPos, {
                    line: endPos.line,
                    ch: startPos.ch + unwrapped.length,
                  });
                }
              } else {
                // Add italic formatting
                const wrapped = `*${selection}*`;
                cm.replaceSelection(wrapped);
                cm.setSelection(
                  { line: startPos.line, ch: startPos.ch + 1 },
                  { line: endPos.line, ch: startPos.ch + 1 + selection.length },
                );
              }
            },
            className: 'markdown-italic-icon',
            title: 'Italic',
          },
          '|',
          {
            name: 'ordered-list',
            action: (editor: any) => {
              const cm = editor.codemirror;
              const cursor = cm.getCursor();
              const line = cm.getLine(cursor.line);

              // Check if already a numbered list
              if (line.match(/^\d+\.\s/)) {
                // Remove numbered list formatting
                cm.replaceRange(
                  line.replace(/^\d+\.\s/, ''),
                  { line: cursor.line, ch: 0 },
                  { line: cursor.line, ch: line.length },
                );
              } else {
                // Remove bullet list if present, then add numbered list
                const cleanLine = line.replace(/^[-*]\s/, '');
                cm.replaceRange(
                  '1. ' + cleanLine,
                  { line: cursor.line, ch: 0 },
                  { line: cursor.line, ch: line.length },
                );
              }
            },
            className: 'markdown-ordered-list-icon',
            title: 'Numbered List',
          },
          {
            name: 'unordered-list',
            action: (editor: any) => {
              const cm = editor.codemirror;
              const cursor = cm.getCursor();
              const line = cm.getLine(cursor.line);

              // Check if already a bullet list
              if (line.match(/^[-*]\s/)) {
                // Remove bullet list formatting
                cm.replaceRange(
                  line.replace(/^[-*]\s/, ''),
                  { line: cursor.line, ch: 0 },
                  { line: cursor.line, ch: line.length },
                );
              } else {
                // Remove numbered list if present, then add bullet list
                const cleanLine = line.replace(/^\d+\.\s/, '');
                cm.replaceRange(
                  '- ' + cleanLine,
                  { line: cursor.line, ch: 0 },
                  { line: cursor.line, ch: line.length },
                );
              }
            },
            className: 'markdown-unordered-list-icon',
            title: 'Bullet List',
          },
        ] as any,
        status: false,
        minHeight,
        maxHeight,
        autofocus: false,
        autoDownloadFontAwesome: false,
      }) as any,
    [placeholder, minHeight, maxHeight],
  );

  // Inject Material-UI icons into toolbar buttons after component mounts
  React.useEffect(() => {
    const injectIcons = () => {
      const boldBtn = document.querySelector('.markdown-bold-icon');
      const italicBtn = document.querySelector('.markdown-italic-icon');
      const orderedListBtn = document.querySelector('.markdown-ordered-list-icon');
      const unorderedListBtn = document.querySelector('.markdown-unordered-list-icon');

      if (boldBtn && !boldBtn.querySelector('svg')) {
        boldBtn.innerHTML = renderToStaticMarkup(<FormatBoldIcon style={{ fontSize: '22px' }} />);
      }
      if (italicBtn && !italicBtn.querySelector('svg')) {
        italicBtn.innerHTML = renderToStaticMarkup(
          <FormatItalicIcon style={{ fontSize: '22px' }} />,
        );
      }
      if (orderedListBtn && !orderedListBtn.querySelector('svg')) {
        orderedListBtn.innerHTML = renderToStaticMarkup(
          <FormatListNumberedIcon style={{ fontSize: '22px' }} />,
        );
      }
      if (unorderedListBtn && !unorderedListBtn.querySelector('svg')) {
        unorderedListBtn.innerHTML = renderToStaticMarkup(
          <FormatListBulletedIcon style={{ fontSize: '22px' }} />,
        );
      }
    };

    // Run after a short delay to ensure SimpleMDE has rendered
    const timer = setTimeout(injectIcons, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`markdown-editor-wrapper ${className}`}>
      <SimpleMDE value={value} onChange={onChange} options={editorOptions} />
    </div>
  );
};
